import { Flex, IconFont, Modal, Popover, Text, Toast, Tooltip } from '@byecode/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type PageAbstract, PageOpenType } from '@lighthouse/core'
import { LANGUAGE_LIST, NO_NAME_PAGE, OperationMenu, SelectLanguage, TooltipText, useAtomData } from '@lighthouse/shared'
import cls from 'classnames'
import copy from 'copy-to-clipboard'
import { find } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCss, useUpdateEffect } from 'react-use'

import { applicationSettingAtom } from '@/atoms/application/state'
import { pageAtomFamily } from '@/atoms/page/state'
import type { MovePagePayload } from '@/atoms/page/types'
import { PageVisibility } from '@/components/PageSetting/Visibility'
import { TagIcon } from '@/components/TagIcon'
import { useLanguage } from '@/hooks/useApplication'
import { useDataSourceDepartments, useDataSourceRoles } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import type { PageItemAction } from '../../constant'
import { menuOperationItems } from '../../constant'
import * as CM from '../../style'
import * as SC from './styles'

interface PageItemProps {
    id: string
    activeId?: string
    level: number
    isEdit: boolean
    style?: React.CSSProperties
    onUpdatePage?: (param: Pick<PageAbstract, 'name' | 'id' | 'open' | 'isHome'>) => Promise<boolean>
    onSetHomePage?: (param: Pick<PageAbstract, 'id' | 'isHome'>) => Promise<boolean>
    onRemovePage?: (id: string) => void
    onToLink?: (page: PageAbstract) => void
    onChangeEdit: (id: string) => void
    onCopyPage: (id: string, lang: string) => Promise<string>
    onMovePage: (params: MovePagePayload) => void
    onCustomRoute?: (id: string) => void
}

const activeCss = {
    color: 'var(--color-gray-600)',
    backgroundColor: 'var(--color-gray-100)',
    borderRadius: '5px'
}

const hoverCss = {
    color: 'var(--color-gray-600)',
    borderRadius: '5px',
    backgroundColor: 'var(--color-gray-100)'
}

const PageItem: React.FC<PageItemProps> = ({
    id,
    activeId = '',
    isEdit,
    level,
    style: propStyle,
    onSetHomePage,
    onUpdatePage,
    onRemovePage,
    onCopyPage,
    onToLink,
    onChangeEdit,
    onCustomRoute
}) => {
    const page = useAtomData(pageAtomFamily(id))
    const { name = '', open = PageOpenType.all, roleIds = [], isHome = false, departmentIds = [], route } = page ?? {}
    const [pageName, setPageName] = useState(name)
    // const [openVisible, setOpenVisible] = useState(false)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [menuOpened, setMenuOpened] = useState(false)
    const [copyLanguageOpened, setCopyLanguageOpened] = useState(false)
    const language = useLanguage()
    const roles = useDataSourceRoles()

    const departments = useDataSourceDepartments()

    const disabledWithVersion = useIsDisabledWithVersion()

    const { attributes, listeners, setNodeRef, node, transform, transition, isOver, active } = useSortable({
        id,
        disabled: isEdit
    })

    const visitableBtnCss = useCss({
        color: activeId === id ? 'var(--color-gray-500)' : 'var(--color-gray-400)',
        '&:hover': hoverCss
    })

    const operationBtnCss = useCss(
        popoverOpen
            ? activeCss
            : {
                  color: activeId === id ? 'var(--color-gray-500)' : 'var(--color-gray-400)',
                  '&:hover': hoverCss
              }
    )

    const homeBtnCss = useCss({
        color: activeId === id ? 'var(--color-gray-500)' : 'var(--color-gray-400)'
    })

    const departmentNames = departmentIds
        .map(departmentId => find(department => department.id === departmentId, departments)?.name)
        .filter(Boolean)
        .join('、')

    const roleNames = roleIds
        .map(roleId => find(role => role.id === roleId, roles)?.name)
        .filter(Boolean)
        .join('、')

    const options = useAtomData(
        applicationSettingAtom,
        useCallback(
            s =>
                s?.language?.list
                    ?.map(item => {
                        const LANG = LANGUAGE_LIST.find(lang => lang.lang === item.lang)
                        return {
                            value: item.lang,
                            label: item.title,
                            icon: LANG?.flag
                        }
                    })
                    .filter(item => item.value !== language) ?? [],
            [language]
        )
    )

    useUpdateEffect(() => {
        setPageName(name)
    }, [name])

    const style = useMemo(
        () => ({
            transform: CSS.Translate.toString(transform && { ...transform, scaleY: 1 }),
            transition,
            ...propStyle
        }),
        [propStyle, transform, transition]
    )

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPageName(e.target.value)
    }, [])

    const handlers = useCallback(
        (pageId: string): Record<PageItemAction, (sender?: React.MouseEvent<HTMLDivElement>) => void> => {
            return {
                async DELETE() {
                    if (isHome) {
                        Toast.warning('主页不能被删除')
                        return
                    }
                    const isConfirm = await Modal.confirm({
                        title: '确认删除页面',
                        content: `确认删除页面「${name ?? '无标题'} 」？`,
                        okText: '删除'
                    })
                    if (isConfirm) {
                        onRemovePage?.(pageId)
                        setPopoverOpen(false)
                    }
                },
                async HOME(ev) {
                    ev?.stopPropagation()
                    if (isHome) {
                        return
                    }
                    await onSetHomePage?.({ id, isHome: true })
                    setPopoverOpen(false)
                },
                COPY_LINK() {
                    const success = copy(window.location.href)
                    if (success) {
                        Toast.success('已复制页面链接')
                    }
                },
                async COPY(ev) {
                    ev?.stopPropagation()
                    await onCopyPage?.(id, language)
                    setPopoverOpen(false)
                },
                RENAME(ev) {
                    ev?.stopPropagation()
                    setPopoverOpen(false)
                    onChangeEdit(id)
                },
                CUSTOM_ROUTE() {
                    onCustomRoute?.(id)
                }
            }
        },
        [isHome, name, onRemovePage, onSetHomePage, id, onCopyPage, language, onChangeEdit, onCustomRoute]
    )

    const handleUpdatePage = useCallback(async () => {
        onChangeEdit(id)
        if (name === pageName) {
            return
        }
        if (!pageName.trim()) {
            setPageName(name)
            return
        }
        const isUpdate = await onUpdatePage?.({ id, name: pageName ?? '未命名', open, isHome })
        if (!isUpdate) {
            setPageName(name)
        }
    }, [onChangeEdit, name, pageName, onUpdatePage, id, open, isHome])

    const handleKeyDown = useCallback(
        (ev: React.KeyboardEvent) => {
            if (ev.key === 'Enter') {
                handleUpdatePage()
            }
        },
        [handleUpdatePage]
    )
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (inputRef && inputRef.current && isEdit) {
            inputRef.current.select()
        }
    }, [isEdit])

    const isActivated = activeId === id

    return (
        <>
            <SC.DragContainer style={style} ref={setNodeRef} {...attributes} {...listeners}>
                <SC.ListItem
                    data-page-id={id}
                    className={cls({ active: isActivated })}
                    onClick={ev => page && onToLink?.(page)}
                    style={{ paddingLeft: 8 + 24 + level * 20 }}
                >
                    <SC.TopBlock>
                        <SC.LeftItem>
                            <IconFont type="PageBroadContent" size={16} />
                            {isEdit ? (
                                <SC.TitleInput>
                                    <input
                                        type="text"
                                        value={pageName}
                                        placeholder="请输入名称"
                                        autoFocus
                                        onKeyDown={handleKeyDown}
                                        onChange={handleChange}
                                        onBlur={() => handleUpdatePage()}
                                        ref={inputRef}
                                    />
                                </SC.TitleInput>
                            ) : (
                                <TooltipText
                                    title={pageName}
                                    render={ref => (
                                        <SC.Name ref={ref} onDoubleClick={() => !disabledWithVersion && onChangeEdit(id)}>
                                            {pageName || NO_NAME_PAGE}
                                        </SC.Name>
                                    )}
                                />
                            )}
                        </SC.LeftItem>
                        <SC.RightItem>
                            {isHome && (
                                <Tooltip title="主页">
                                    <div>
                                        <TagIcon radius={5} iconSize={12} size={12} className={homeBtnCss} icon="Home" />
                                    </div>
                                </Tooltip>
                            )}

                            {open !== PageOpenType.all && (
                                <Tooltip
                                    title={
                                        open === PageOpenType.login ? (
                                            '登录后可见'
                                        ) : (
                                            <Flex
                                                direction="column"
                                                style={{ maxWidth: 182, lineHeight: '20px', padding: '4px 8px', whiteSpace: 'pre-wrap' }}
                                            >
                                                {roleNames && (
                                                    <Text>
                                                        以下角色可见：
                                                        {roleNames}。
                                                    </Text>
                                                )}
                                                {departmentNames && (
                                                    <Text>
                                                        以下部门可见：
                                                        {departmentNames}。
                                                    </Text>
                                                )}
                                                {!roleNames && !departmentNames && <Text>未指定角色或部门</Text>}
                                            </Flex>
                                        )
                                    }
                                >
                                    <div>
                                        <TagIcon radius={5} iconSize={12} size={12} icon="Lock" className={visitableBtnCss} />
                                    </div>
                                </Tooltip>
                            )}

                            {route && (
                                <Tooltip
                                    title={
                                        <div style={{ lineHeight: '20px' }}>
                                            <Text>点击编辑自定义页面路径</Text>
                                            <Text color="var(--color-gray-400)">当前页面路径：{route}</Text>
                                        </div>
                                    }
                                >
                                    <TagIcon
                                        radius={5}
                                        iconSize={12}
                                        size={12}
                                        color="var(--color-black)"
                                        icon="PropertyLink"
                                        onClick={e => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            onCustomRoute?.(id)
                                        }}
                                    />
                                </Tooltip>
                            )}

                            {!disabledWithVersion && (
                                <TagIcon
                                    data-ignore-click-away
                                    onClick={ev => {
                                        setPopoverOpen(true)
                                        ev.stopPropagation()
                                    }}
                                    size={20}
                                    color="var(--color-black)"
                                    radius={5}
                                    className={cls({ operationBtnCss, dotsThree: true, activeDots: popoverOpen })}
                                    icon="DotsThree"
                                />
                            )}
                        </SC.RightItem>
                    </SC.TopBlock>

                    {/* WARNING 注意更换popover时，需要将copy link事件里指定的focus元素替换 */}
                    <Popover
                        opened={popoverOpen}
                        returnFocus={false}
                        onChange={setPopoverOpen}
                        width="auto"
                        withinPortal
                        position="right-start"
                    >
                        <Popover.AnchorEl anchorEl={node.current} />
                        <Popover.Dropdown>
                            <OperationMenu
                                onClose={() => setPopoverOpen(false)}
                                handlers={handlers}
                                menuItems={menuOperationItems}
                                isRename={false}
                                id={id}
                                value={pageName}
                                extra={
                                    <>
                                        <SelectLanguage
                                            options={options}
                                            trigger="hover"
                                            position="right-start"
                                            opened={copyLanguageOpened}
                                            onChange={setCopyLanguageOpened}
                                            targetComponent={
                                                <SC.Item className={cls({ active: copyLanguageOpened })}>
                                                    <Flex gap="8px">
                                                        <CM.Icon type="Copy" color="var(--color-gray-400)" />
                                                        <Text size={14}> 复制到其他语言版本 </Text>
                                                    </Flex>
                                                    <Flex>
                                                        <CM.Icon type="ArrowRightSmall" color="var(--color-gray-400)" />
                                                    </Flex>
                                                </SC.Item>
                                            }
                                            onClick={async lang => {
                                                await onCopyPage(id, lang)
                                                setPopoverOpen(false)
                                            }}
                                        />
                                        <Popover
                                            opened={menuOpened}
                                            onClose={() => setMenuOpened(false)}
                                            onChange={setMenuOpened}
                                            trigger="hover"
                                            withinPortal
                                            position="left-start"
                                            width="auto"
                                        >
                                            <Popover.Target>
                                                <SC.Item className={cls({ active: menuOpened })}>
                                                    <Flex gap="8px">
                                                        <CM.Icon type="Eye" color="var(--color-gray-400)" />
                                                        <Text size={14}> 页面可见性 </Text>
                                                    </Flex>
                                                    <Flex>
                                                        <CM.Icon type="ArrowRightSmall" color="var(--color-gray-400)" />
                                                    </Flex>
                                                </SC.Item>
                                            </Popover.Target>
                                            <Popover.Dropdown>
                                                <PageVisibility pageId={id} mode="none" style={{ width: 280, padding: 0 }} />
                                            </Popover.Dropdown>
                                        </Popover>
                                    </>
                                }
                            />
                        </Popover.Dropdown>
                    </Popover>
                </SC.ListItem>
                {/* {isOver && active?.data.current?.type !== 'folder' && (
                    <DropIndicator depth={level ?? 0} position={path(['data', 'dropPosition'], active) || 'bottom'} />
                )} */}
            </SC.DragContainer>
        </>
    )
}

export default React.memo(PageItem)
